import { Matrix, Vector3 } from "@babylonjs/core/Maths/math.vector";
import { PointerInfo } from "../view/InputState";
import { CoordinatesType } from "../view/View";
export const InteractorType = {
    None: 0,
    Dragger: 1,
    Button: 2
};
export class Interactor {
    get element() {
        return (this.mElement);
    }
    get visible() {
        return (this.mVisible);
    }
    set visible(pValue) {
        if (this.mVisible !== pValue) {
            this.mVisible = pValue;
            this.updateVisibility();
        }
    }
    get type() {
        return (this.mType);
    }
    set type(pValue) {
        if (this.mType !== pValue) {
            this.mType = pValue;
            this.mElement.style.pointerEvents = this.mType === InteractorType.None ? "none" : "all";
            this.updateViewPosition();
        }
    }
    constructor(pElement, pViewer) {
        this.onClick = (pEvent) => {
            if (this.mType === InteractorType.Button && this.onTriggerAction != null) {
                this.trigger(pEvent);
            }
            else
                this.mViewer._forwardEvent(pEvent, false);
        };
        this.onPointerDown = (pEvent) => {
            let tForwardEvent = true;
            let tStartDragging = false;
            const tState = this.mViewer.inputState;
            const tIsPrimaryTouch = !PointerInfo.IsMousePointer(pEvent) && tState.pointers.size === 0;
            const tIsPrimaryMouse = PointerInfo.IsPrimaryMousePointer(pEvent);
            if (this.onTriggerAction != null && (tIsPrimaryTouch || tIsPrimaryMouse)) {
                if (this.mType === InteractorType.Dragger)
                    tStartDragging = true;
                else
                    tForwardEvent = false; //do not foward primary events for buttons
            }
            if (tStartDragging) {
                this.mViewer.app.tools._setActiveDraggerInteractor(this, pEvent.pointerId);
                this.trigger(pEvent);
            }
            if (tForwardEvent)
                this.mViewer._forwardEvent(pEvent, false);
        };
        this.onMouseWheel = (pEvent) => {
            this.mViewer._forwardEvent(pEvent, false);
        };
        this.onContextMenu = (pEvent) => {
            //prevent browser context menu
            pEvent.preventDefault();
        };
        this.mElement = pElement;
        this.mViewer = pViewer;
        this.mView = null;
        this.mVisible = true;
        this.mType = InteractorType.Dragger;
        this.mManager = null;
        this.mDisableUpdate = false;
        this.mPendingNewView = undefined; //has to be undefined, see _finishDragging()
        this.position = Vector3.Zero();
        this.posOffsetDirection = null;
        this.posOffset = 0;
        this.worldMatrix = null;
        this.customCssTransform = "";
        this.transform3d = false;
        this.transform3dRotation = null;
        this.onTriggerAction = null;
        this.onBeforeUpdateViewPosition = null;
        //set some required styles
        this.mElement.style.pointerEvents = "all";
        this.mElement.style.display = "block";
        this.mElement.style.position = "absolute";
        //register events
        this.mElement.addEventListener("click", this.onClick, false);
        this.mElement.addEventListener("pointerdown", this.onPointerDown, false);
        this.mElement.addEventListener("wheel", this.onMouseWheel, { capture: false, passive: false });
        this.mElement.addEventListener("contextmenu", this.onContextMenu, false);
    }
    updateViewPosition() {
        if (this.mView == null || this.mDisableUpdate)
            return (false);
        if (this.onBeforeUpdateViewPosition != null) {
            this.mDisableUpdate = true; //prevent recursive updates
            this.onBeforeUpdateViewPosition(this.mView);
            this.mDisableUpdate = false;
        }
        if (!this.mVisible)
            return (false);
        const tProjPos = this.mView.projectPoint2(this.position, CoordinatesType.Viewer, this.worldMatrix);
        if (tProjPos != null) {
            //add offset
            if (this.posOffsetDirection != null && this.posOffset !== 0) {
                const tOffsetPos = this.position.add(this.posOffsetDirection.scale(0.1));
                const tProjOffsetPos = this.mView.projectPoint2(tOffsetPos, CoordinatesType.Viewer, this.worldMatrix);
                if (tProjOffsetPos != null) {
                    const tDir = tProjOffsetPos.subtract(tProjPos);
                    tDir.normalize();
                    tDir.scaleInPlace(this.posOffset);
                    tProjPos.addInPlace(tDir);
                }
            }
            this.mElement.style.transform = this.customCssTransform;
            if (this.transform3d) {
                const tTransform = this.mElement.style.transform;
                this.mElement.style.transform = tTransform + " " + this.getCssTranformMatrix(5);
                /**
                 * Workaround for Firefox: Firefox sometimes clips of parts of the interactor. (with version 78 it seems that only the picking area is to small)
                 * Forcing an additional delayed update by slightly modifying the transform matrix seems to avoid this.
                 * TODO: check if this is still required witch firefox version > 78.
                 */
                if (!this.mPendingAnimationFrame) {
                    this.mPendingAnimationFrame = true;
                    requestAnimationFrame(() => {
                        this.mElement.style.transform = tTransform + " " + this.getCssTranformMatrix(6);
                        this.mPendingAnimationFrame = false;
                    });
                }
            }
            this.mElement.style.left = Math.round(tProjPos.x).toString() + "px";
            this.mElement.style.top = Math.round(tProjPos.y).toString() + "px";
            this.mElement.style.display = "block";
            return (true);
        }
        else
            this.mElement.style.display = "none";
        return (false);
    }
    //// internal //////////////////////////////////////////////////////////////////////////////
    /** @internal */
    _setManager(pManager) {
        this.mManager = pManager;
    }
    /** @internal */
    _setView(pView) {
        if (this.mView === pView)
            return;
        if (this.mViewer.app.tools._activeDraggerInteractor === this) {
            //view change is not allowed for active dragger, this is necessary for IOS and firefox
            this.mPendingNewView = pView;
            //delay the style change, this is necessary for firefox (tested with version 97.0.1)
            //otherwise firefox would not send any further pointer events
            setTimeout(() => {
                if (pView == null) { //make interactor invisible in this case
                    this.mElement.style.display = "none";
                }
            });
        }
        else {
            if (this.mView != null)
                this.mView.overlayDiv.removeChild(this.mElement);
            this.mView = pView;
            if (this.mView != null)
                this.mView.overlayDiv.appendChild(this.mElement);
        }
    }
    /** @internal */
    _finishDragging() {
        if (this.mPendingNewView !== undefined) {
            this._setView(this.mPendingNewView);
            this.mPendingNewView === undefined;
            //restore visibility
            this.updateVisibility();
        }
    }
    updateVisibility() {
        if (this.mManager == null || this.mManager.enabled) {
            this.mElement.style.display = this.mVisible ? "block" : "none";
            this.updateViewPosition();
        }
        else {
            this.mElement.style.display = "none";
        }
    }
    getCssTranformMatrix(pPrecision) {
        const tMatrix = gTmpMatrix;
        if (this.mView != null) {
            if (this.transform3dRotation != null) {
                this.transform3dRotation.toRotationMatrix(tMatrix);
                tMatrix.multiplyToRef(this.mView.camera.getViewMatrix(), tMatrix);
            }
            else {
                tMatrix.copyFrom(this.mView.camera.getViewMatrix());
            }
        }
        //apply mirror matrix
        tMatrix.multiplyToRef(Interactor.MIRROR_X_MATRIX, tMatrix);
        //flip x component
        tMatrix.setRowFromFloats(0, -tMatrix.m[0], -tMatrix.m[1], -tMatrix.m[2], tMatrix.m[3]);
        //remove translation
        tMatrix.setTranslationFromFloats(0, 0, 0);
        //build css matrix string
        let tCssMatrix = "matrix3d(";
        for (let i = 0; i < 16; ++i) {
            tCssMatrix += tMatrix.m[i].toFixed(pPrecision);
            if (i !== 15)
                tCssMatrix += ",";
        }
        tCssMatrix += ")";
        return (tCssMatrix);
    }
    trigger(pEvent) {
        if (this.onTriggerAction != null) {
            this.mViewer._forwardEvent(pEvent, true); //update pointer position before triggering
            this.onTriggerAction();
            this.mViewer.app.tools.eventInteractorTriggered.trigger(this);
        }
    }
}
//// private ///////////////////////////////////////////////////////////////////////////////
Interactor.MIRROR_X_MATRIX = Matrix.Scaling(-1, 1, 1);
//// temp variables ////////////////////////////////////////////////////////////////////////////
const gTmpMatrix = Matrix.Identity();
